import { ChevronDown, Download, FileDown, FileSpreadsheet, FileText } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SimpleTooltip } from '@/components/ui/tooltip';
import type { CallsExportOption } from '@/types/export.types';

interface DownloadDropdownProps {
  onDownload?: (
    format: 'csv' | 'xlsx',
    callsOption?: CallsExportOption,
    includeEligibilityChecks?: boolean,
  ) => Promise<void>;
  disabled?: boolean;
  buttonClassName?: string;
  onlyShowIcon?: boolean;
  variant?: 'default' | 'floating';
  hideChevron?: boolean;
  workflowType?: string;
  // SOB-related props
  showSOBOption?: boolean;
  onDownloadSOB?: () => Promise<void>;
  isDownloadingSOB?: boolean;
  sobLabel?: string;
  onDownloadPDF?: () => Promise<void> | void;
  pdfLabel?: string;
}

export const DownloadDropdown: React.FC<DownloadDropdownProps> = ({
  onDownload,
  disabled = false,
  buttonClassName = '',
  onlyShowIcon = false,
  variant = 'default',
  hideChevron = false,
  workflowType,
  showSOBOption = false,
  onDownloadSOB,
  isDownloadingSOB = false,
  sobLabel = 'Download SOB',
  onDownloadPDF,
  pdfLabel = 'Export as PDF',
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'xlsx' | 'pdf' | null>(null);
  const [callsOption, setCallsOption] = useState<CallsExportOption>('none');
  const [includeEligibilityChecks, setIncludeEligibilityChecks] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleDownload = async (format: 'csv' | 'xlsx') => {
    if (isDownloading) return;

    setIsDownloading(true);
    setDownloadFormat(format);

    try {
      if (onDownload) {
        await onDownload(format, callsOption, includeEligibilityChecks);
      }
    } finally {
      setIsDownloading(false);
      setDownloadFormat(null);
    }
  };

  const handleDownloadPDF = async () => {
    if (isDownloading || !onDownloadPDF) return;

    setIsDownloading(true);
    setDownloadFormat('pdf');

    try {
      await onDownloadPDF();
    } finally {
      setIsDownloading(false);
      setDownloadFormat(null);
    }
  };

  const isFloating = variant === 'floating';
  const hasDataDownloadOptions = Boolean(onDownload);
  const hasAdditionalDownloadOptions = Boolean(onDownloadPDF) || showSOBOption;

  // Only show tooltip when icon-only mode and dropdown is closed
  const tooltipContent = onlyShowIcon && !isDropdownOpen ? 'Export options' : null;

  return (
    <SimpleTooltip content={tooltipContent} side="bottom">
      <div>
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant={isFloating ? 'ghost' : 'outline'}
              size="sm"
              className={`flex items-center gap-2 ${buttonClassName}`}
              disabled={disabled || isDownloading || isDownloadingSOB}
            >
              <Download className="w-4 h-4 shrink-0" />
              {!onlyShowIcon &&
                (isDownloading ? `Downloading ${downloadFormat?.toUpperCase()}...` : 'Download')}
              {!hideChevron && !onlyShowIcon && <ChevronDown className="w-4 h-4 ml-1 shrink-0" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wide">
              Export Options
            </DropdownMenuLabel>
            {hasDataDownloadOptions && workflowType !== 'pqc' && (
              <>
                <div className="flex items-center space-x-2 px-2 py-1.5">
                  <Checkbox
                    id="include-calls"
                    checked={callsOption === 'include'}
                    onCheckedChange={checked => setCallsOption(checked ? 'include' : 'none')}
                    disabled={isDownloading}
                  />
                  <label
                    htmlFor="include-calls"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
                  >
                    Include Calls
                  </label>
                </div>
                <div className="flex items-center space-x-2 px-2 py-1.5">
                  <Checkbox
                    id="only-calls"
                    checked={callsOption === 'only'}
                    onCheckedChange={checked => setCallsOption(checked ? 'only' : 'none')}
                    disabled={isDownloading}
                  />
                  <label
                    htmlFor="only-calls"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
                  >
                    Only Calls
                  </label>
                </div>
              </>
            )}
            {workflowType === 'medbv' && (
              <div className="flex items-center space-x-2 px-2 py-1.5">
                <Checkbox
                  id="include-eligibility-checks"
                  checked={includeEligibilityChecks}
                  onCheckedChange={checked => setIncludeEligibilityChecks(checked as boolean)}
                  disabled={isDownloading}
                />
                <label
                  htmlFor="include-eligibility-checks"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
                >
                  Include Eligibility Checks
                </label>
              </div>
            )}
            {hasDataDownloadOptions && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleDownload('csv')}
                  disabled={isDownloading}
                  className="flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDownload('xlsx')}
                  disabled={isDownloading}
                  className="flex items-center gap-2"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export as Excel
                </DropdownMenuItem>
              </>
            )}
            {hasDataDownloadOptions && hasAdditionalDownloadOptions && <DropdownMenuSeparator />}
            {onDownloadPDF && (
              <DropdownMenuItem
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {pdfLabel}
              </DropdownMenuItem>
            )}
            {showSOBOption && (
              <>
                <DropdownMenuItem
                  onClick={async () => {
                    if (onDownloadSOB) await onDownloadSOB();
                  }}
                  disabled={isDownloadingSOB}
                  className="flex items-center gap-2"
                >
                  <FileDown className="w-4 h-4" />
                  {sobLabel}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </SimpleTooltip>
  );
};
